import {
  Badge,
  Box,
  Button,
  Card,
  Field,
  Flex,
  HStack,
  Heading,
  InfoBox,
  Input,
  RefreshButton,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  notify,
} from "components/ui";
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "components/ui/dialog";
import DeleteConfirmDialog from "components/common/DeleteConfirmDialog";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import {
  MdAdd,
  MdArrowDownward,
  MdArrowUpward,
  MdDelete,
  MdEdit,
} from "react-icons/md";
import { customerCompanyAdminService } from "services/customerCompanyAdminService";
import type { PromptShortcut } from "services/promptService";
import { getThemeColor } from "theme/colors";

interface PromptShortcutsTabProps {
  customerCompanyId: string;
}

interface ShortcutForm {
  name: string;
  description: string;
  body: string;
  icon: string;
  isActive: boolean;
}

const EMPTY_FORM: ShortcutForm = {
  name: "",
  description: "",
  body: "",
  icon: "zap",
  isActive: true,
};

/**
 * Prompt shortcuts shown to this company's widget users.
 *
 * Translations are intentionally absent: the backend discards any translations sent at
 * CustomerCompany scope, so offering the editor here would silently throw the work away. Only
 * tenant-wide shortcuts (/admin/configure/prompt-shortcuts) support translations.
 */
const PromptShortcutsTab = ({ customerCompanyId }: PromptShortcutsTabProps) => {
  const { t } = useTranslation();

  const [shortcuts, setShortcuts] = useState<PromptShortcut[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<PromptShortcut | null>(null);
  const [form, setForm] = useState<ShortcutForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<PromptShortcut | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setShortcuts(
        await customerCompanyAdminService.listPromptShortcuts(
          customerCompanyId,
        ),
      );
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.customerCompanies.prompts.loadFailed", {
            defaultValue: "Failed to load prompt shortcuts.",
          }),
      );
    } finally {
      setLoading(false);
    }
  }, [customerCompanyId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const reportFailure = (err: any) =>
    notify({
      type: "error",
      title: t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      description: err.response?.data?.error,
    });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setEditorOpen(true);
  };

  const openEdit = (shortcut: PromptShortcut) => {
    setEditing(shortcut);
    setForm({
      name: shortcut.name,
      description: shortcut.description ?? "",
      body: shortcut.body,
      icon: shortcut.icon ?? "zap",
      isActive: shortcut.isActive,
    });
    setFormError(null);
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.body.trim()) {
      setFormError(
        t("app.customerCompanies.prompts.requiredFields", {
          defaultValue: "Name and prompt body are required.",
        }),
      );
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        body: form.body.trim(),
        icon: form.icon.trim() || undefined,
        isActive: form.isActive,
      };
      if (editing) {
        const updated = await customerCompanyAdminService.updatePromptShortcut(
          customerCompanyId,
          editing.id,
          payload,
        );
        setShortcuts((prev) =>
          prev.map((s) => (s.id === updated.id ? updated : s)),
        );
      } else {
        const created = await customerCompanyAdminService.createPromptShortcut(
          customerCompanyId,
          { ...payload, sortOrder: shortcuts.length },
        );
        setShortcuts((prev) => [...prev, created]);
      }
      setEditorOpen(false);
      notify({
        type: "success",
        title: t("app.customerCompanies.prompts.saved", {
          defaultValue: "Prompt shortcut saved.",
        }),
      });
    } catch (err: any) {
      setFormError(
        err.response?.data?.error ||
          t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (
    shortcut: PromptShortcut,
    isActive: boolean,
  ) => {
    setPendingId(shortcut.id);
    try {
      const updated =
        await customerCompanyAdminService.togglePromptShortcutStatus(
          customerCompanyId,
          shortcut.id,
          isActive,
        );
      setShortcuts((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s)),
      );
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setPendingId(null);
    }
  };

  /** Swap a shortcut with its neighbour and persist the whole order. */
  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= shortcuts.length) return;

    const reordered = [...shortcuts];
    [reordered[index], reordered[target]] = [
      reordered[target],
      reordered[index],
    ];
    setShortcuts(reordered);

    try {
      await customerCompanyAdminService.reorderPromptShortcuts(
        customerCompanyId,
        reordered.map((s, i) => ({ id: s.id, sortOrder: i })),
      );
    } catch (err: any) {
      reportFailure(err);
      void load();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await customerCompanyAdminService.deletePromptShortcut(
        customerCompanyId,
        deleteTarget.id,
      );
      setShortcuts((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      notify({
        type: "success",
        title: t("app.customerCompanies.prompts.deleted", {
          defaultValue: "Prompt shortcut deleted.",
        }),
      });
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Card.Root
      borderWidth="1px"
      borderColor={getThemeColor("card.border")}
      borderRadius="md"
    >
      <Card.Body>
        <Stack gap={3}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <Stack gap={1}>
              <Heading size="sm">
                {t("app.customerCompanies.prompts.title", {
                  defaultValue: "Prompt shortcuts",
                })}
              </Heading>
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.prompts.subtitle", {
                  defaultValue:
                    "One-click prompts shown in the widget to this company's users.",
                })}
              </Text>
            </Stack>
            <HStack gap={2}>
              <RefreshButton onClick={load} loading={loading} />
              <Button intent="primary" onClick={openCreate}>
                <MdAdd />{" "}
                {t("app.customerCompanies.prompts.add", {
                  defaultValue: "Add shortcut",
                })}
              </Button>
            </HStack>
          </Flex>

          {error && (
            <InfoBox variant="error">
              <Text color="red.700">{error}</Text>
            </InfoBox>
          )}

          {loading ? (
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.customerCompanies.prompts.loading", {
                defaultValue: "Loading shortcuts…",
              })}
            </Text>
          ) : shortcuts.length === 0 ? (
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.customerCompanies.prompts.empty", {
                defaultValue: "No prompt shortcuts for this company yet.",
              })}
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.prompts.columns.name", {
                        defaultValue: "Name",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.prompts.columns.body", {
                        defaultValue: "Prompt",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.prompts.columns.icon", {
                        defaultValue: "Icon",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.common.active", { defaultValue: "Active" })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.common.actions", { defaultValue: "Actions" })}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {shortcuts.map((shortcut, index) => (
                    <Table.Row key={shortcut.id}>
                      <Table.Cell>
                        <Stack gap={0}>
                          <Text fontSize="sm" fontWeight="medium">
                            {shortcut.name}
                          </Text>
                          {shortcut.description && (
                            <Text
                              fontSize="xs"
                              color="var(--text-secondary-color)"
                            >
                              {shortcut.description}
                            </Text>
                          )}
                        </Stack>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs">{shortcut.body}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant="subtle">{shortcut.icon ?? "—"}</Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Switch
                          checked={shortcut.isActive}
                          disabled={pendingId === shortcut.id}
                          onCheckedChange={(e: any) =>
                            handleToggleActive(shortcut, e.checked)
                          }
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <HStack gap={1}>
                          <Button
                            intent="ghost"
                            disabled={index === 0}
                            onClick={() => handleMove(index, -1)}
                            aria-label={t(
                              "app.customerCompanies.prompts.moveUp",
                              {
                                defaultValue: "Move up",
                              },
                            )}
                          >
                            <MdArrowUpward />
                          </Button>
                          <Button
                            intent="ghost"
                            disabled={index === shortcuts.length - 1}
                            onClick={() => handleMove(index, 1)}
                            aria-label={t(
                              "app.customerCompanies.prompts.moveDown",
                              { defaultValue: "Move down" },
                            )}
                          >
                            <MdArrowDownward />
                          </Button>
                          <Button
                            intent="ghost"
                            onClick={() => openEdit(shortcut)}
                          >
                            <MdEdit />{" "}
                            {t("app.common.edit", { defaultValue: "Edit" })}
                          </Button>
                          <Button
                            intent="ghost"
                            onClick={() => setDeleteTarget(shortcut)}
                          >
                            <MdDelete />{" "}
                            {t("app.common.delete", { defaultValue: "Delete" })}
                          </Button>
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Stack>
      </Card.Body>

      <DialogRoot
        open={editorOpen}
        onOpenChange={(e: { open: boolean }) =>
          e.open ? undefined : setEditorOpen(false)
        }
        size="lg"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t("app.customerCompanies.prompts.edit", {
                    defaultValue: "Edit shortcut",
                  })
                : t("app.customerCompanies.prompts.add", {
                    defaultValue: "Add shortcut",
                  })}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap={4}>
              {formError && (
                <InfoBox variant="error">
                  <Text color="red.700">{formError}</Text>
                </InfoBox>
              )}

              <Field
                label={t("app.customerCompanies.prompts.fields.name", {
                  defaultValue: "Name",
                })}
                required
              >
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  maxLength={255}
                />
              </Field>

              <Field
                label={t("app.customerCompanies.prompts.fields.description", {
                  defaultValue: "Description (optional)",
                })}
              >
                <Input
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  maxLength={1000}
                />
              </Field>

              <Field
                label={t("app.customerCompanies.prompts.fields.body", {
                  defaultValue: "Prompt body",
                })}
                helperText={t("app.customerCompanies.prompts.fields.bodyHelp", {
                  defaultValue:
                    "The message sent to the assistant when a user picks this shortcut.",
                })}
                required
              >
                <Textarea
                  value={form.body}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, body: e.target.value }))
                  }
                  rows={5}
                />
              </Field>

              <Field
                label={t("app.customerCompanies.prompts.fields.icon", {
                  defaultValue: "Icon",
                })}
                helperText={t("app.customerCompanies.prompts.fields.iconHelp", {
                  defaultValue:
                    "Lucide icon name, e.g. zap, search, file-text.",
                })}
              >
                <Input
                  value={form.icon}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, icon: e.target.value }))
                  }
                  maxLength={64}
                />
              </Field>

              <HStack justify="space-between">
                <Text fontWeight="medium">
                  {t("app.common.active", { defaultValue: "Active" })}
                </Text>
                <Switch
                  checked={form.isActive}
                  onCheckedChange={(e: any) =>
                    setForm((prev) => ({ ...prev, isActive: e.checked }))
                  }
                />
              </HStack>

              <Text fontSize="xs" color="var(--text-secondary-color)">
                {t("app.customerCompanies.prompts.noTranslationsHint", {
                  defaultValue:
                    "Company shortcuts are not translated. Use tenant-wide shortcuts if you need multiple languages.",
                })}
              </Text>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button intent="secondary" onClick={() => setEditorOpen(false)}>
              {t("app.common.cancel")}
            </Button>
            <Button intent="primary" onClick={handleSave} disabled={saving}>
              {saving
                ? t("app.common.saving", { defaultValue: "Saving..." })
                : t("app.common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      <DeleteConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title={t("app.customerCompanies.prompts.deleteTitle", {
          defaultValue: "Delete shortcut",
        })}
        message={t("app.customerCompanies.prompts.deleteConfirm", {
          defaultValue:
            "This shortcut will stop appearing in the widget for this company's users.",
        })}
      />
    </Card.Root>
  );
};

export default PromptShortcutsTab;
